// PodpisDokumentyPanel — podpis_dok_v1 (19. 10. 2026, GO Mirka, Kronika podpis 1914)
// Záložka „Na podpis" na karte zamestnanca. Vedenie (admin, konateľ, vedúca office) tu vytvorí dokument,
// ktorý zamestnanec podpíše OSOBNE na podpisovej stránke (iPad, Safari): prečíta celý, odklikne oboznámenie,
// ukáže doklad a podpíše sa. Režim B ústavy 141.1 — nič sa tu nepredvypĺňa ani nepodpisuje za neho.
// Prvý dokument: Prevzatie OOPP a náradia. Každý podpis je nová verzia v `documents` (0.J).
import React from 'react'
import { COLORS, TYPO, SPACE, RADIUS } from '../theme.js'
import { Modal } from './Modal.jsx'
import { Btn, BtnAdd, BtnGhost } from './Btn.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useToast } from './Toast.jsx'
import { useConfirm } from './ConfirmDialog.jsx'

const STAV = {
  na_podpis:       { t: 'čaká na podpis', f: '#A8411A' },
  podpisany:       { t: 'podpísaný',      f: '#15803d' },
  podpisany_zdroj: { t: 'pôvodná verzia (podpísaná ďalej)', f: '#6b7280' },
  zrusena:         { t: 'výzva zrušená',  f: '#6b7280' },
}
const casSk = (s) => { if (!s) return ''; try { return new Date(String(s).replace(' ', 'T') + 'Z').toLocaleString('sk-SK') } catch (_) { return s } }
const prazdny = () => ({ nazov: '', druh: 'oopp', pocet: 1, velkost: '', pozn: '' })
const dnes = () => new Date().toLocaleString('en-CA', { timeZone: 'Europe/Bratislava' }).slice(0, 10)

export default function PodpisDokumentyPanel({ employeeId, employeeName }) {
  const toast = useToast()
  const confirm = useConfirm()
  const [data, setData] = React.useState(null)
  const [chyba, setChyba] = React.useState('')
  const [nove, setNove] = React.useState(false)
  const [polozky, setPolozky] = React.useState([prazdny()])
  const [datum, setDatum] = React.useState(dnes())
  const [pritomny, setPritomny] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [pdfUrl, setPdfUrl] = React.useState({})
  const [overenie, setOverenie] = React.useState({})
  const [adresa, setAdresa] = React.useState(null)

  const nacitaj = React.useCallback(() => {
    setChyba('')
    fetchAuth('/api/podpis/dok/zamestnanec/' + encodeURIComponent(employeeId))
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (!ok) { setChyba(d.error || 'Nenačítalo sa.'); setData(null) } else setData(d) })
      .catch(() => setChyba('Spojenie zlyhalo.'))
  }, [employeeId])
  React.useEffect(() => { nacitaj() }, [nacitaj])
  React.useEffect(() => {
    fetchAuth('/api/podpis/adresa').then(r => r.ok ? r.json() : null).then(setAdresa).catch(() => setAdresa(null))
  }, [])

  const zmen = (i, k, v) => setPolozky(p => p.map((x, j) => j === i ? { ...x, [k]: v } : x))
  const dirty = nove && polozky.some(p => p.nazov.trim())

  const vytvor = async () => {
    const pol = polozky.filter(p => p.nazov.trim())
    if (!pol.length) { toast.error('Doplň aspoň jednu položku — názov je povinný.'); return }
    setBusy(true)
    try {
      const r = await fetchAuth('/api/podpis/dok/oopp', { method: 'POST', body: JSON.stringify({ employee_id: employeeId, polozky: pol, datum, pritomny }) })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || ('HTTP ' + r.status))
      toast.success(d.cislo + ' vytvorený — zamestnanec ho podpíše na podpisovej stránke')
      setNove(false); setPolozky([prazdny()]); setPritomny(''); nacitaj()
    } catch (e) { toast.error('Nepodarilo sa: ' + e.message) } finally { setBusy(false) }
  }
  // iOS blokuje window.open po await (Kronika 1385) — PDF sa najprv stiahne a ukáže sa odkaz
  const pripravPdf = async (id) => {
    try {
      const r = await fetchAuth('/api/podpis/dok/' + encodeURIComponent(id) + '/pdf')
      if (!r.ok) throw new Error('HTTP ' + r.status)
      const u = URL.createObjectURL(await r.blob())
      setPdfUrl(x => ({ ...x, [id]: u }))
    } catch (e) { toast.error('PDF sa nenačítalo: ' + e.message) }
  }
  const over = async (id) => {
    try {
      const r = await fetchAuth('/api/podpis/dok/' + encodeURIComponent(id) + '/overit')
      const d = await r.json()
      setOverenie(x => ({ ...x, [id]: d }))
    } catch (e) { toast.error('Overenie zlyhalo') }
  }
  const zrus = async (row) => {
    const ok = await confirm({ title: 'Zrušiť výzvu na podpis?', message: row.cislo + ' — zamestnanec ho už na podpisovej stránke neuvidí. Záznam sa nemaže.', confirmLabel: 'Zrušiť výzvu' })
    if (!ok) return
    const r = await fetchAuth('/api/podpis/dok/' + encodeURIComponent(row.id) + '/zrusit', { method: 'POST' })
    const d = await r.json().catch(() => ({}))
    if (!r.ok) { toast.error(d.error || 'Nepodarilo sa'); return }
    toast.success('Výzva zrušená'); nacitaj()
  }

  if (chyba) return <div style={{ padding: SPACE.md, color: COLORS.muted, fontSize: TYPO.sizes.sm }}>{chyba}</div>
  if (!data) return <div style={{ padding: SPACE.md, color: COLORS.muted, fontSize: TYPO.sizes.sm }}>Načítavam…</div>
  const docs = data.dokumenty || []
  const bunka = { padding: '7px 8px', borderBottom: `1px solid ${COLORS.line}`, verticalAlign: 'top', fontSize: TYPO.sizes.sm }

  return (
    <div style={{ paddingTop: SPACE.md }}>
      <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted, maxWidth: 760, lineHeight: 1.55, marginBottom: SPACE.md }}>
        Dokumenty, ktoré {employeeName || 'zamestnanec'} podpisuje <b>osobne</b> na podpisovej stránke (iPad v Safari):
        prečíta celý dokument, potvrdí oboznámenie, ukáže doklad totožnosti a podpíše sa. Totožnosť overí PIN alebo kód z SMS.
        Matrix uloží odtlačok dokumentu, čas, zariadenie a podpis; vyhotovenie dostane zamestnanec hneď.
        {adresa && adresa.cesta && <> Podpisová stránka: <code style={{ fontSize: TYPO.sizes.xs }}>{window.location.origin + adresa.cesta}</code></>}
      </div>
      {data.vedenie && (
        <div style={{ marginBottom: SPACE.md }}>
          <BtnAdd onClick={() => { setNove(true); setDatum(dnes()) }}>Prevzatie OOPP a náradia</BtnAdd>
        </div>
      )}
      {docs.length === 0
        ? <div style={{ color: COLORS.muted, fontSize: TYPO.sizes.sm, padding: SPACE.sm }}>Zatiaľ žiadny dokument na podpis.</div>
        : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['Číslo', 'Dokument', 'Verzia', 'Stav', 'Podpis', 'Vyhotovenie', ''].map(h =>
              <th key={h} style={{ ...bunka, fontSize: TYPO.sizes.xs, color: COLORS.muted, textAlign: 'left', fontWeight: TYPO.weights.semibold }}>{h}</th>)}</tr></thead>
            <tbody>{docs.map(row => {
              const st = STAV[row.status] || { t: row.status, f: COLORS.muted }
              const ov = overenie[row.id]
              return (
                <tr key={row.id}>
                  <td style={{ ...bunka, fontFamily: TYPO.mono, whiteSpace: 'nowrap' }}>{row.cislo}</td>
                  <td style={bunka}>{row.doc_type}<div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{row.datum} · vytvoril {row.created_by}</div></td>
                  <td style={bunka}>v{row.doc_version}</td>
                  <td style={{ ...bunka, color: st.f, fontWeight: TYPO.weights.semibold }}>{st.t}</td>
                  <td style={bunka}>{row.ep_cas
                    ? <>{casSk(row.ep_cas)}<div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{row.ep_sposob} · {row.ep_nastroj} · doklad {row.ep_doklad_zhoda}</div></>
                    : (row.ep_pritomny ? <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>pri podpise: {row.ep_pritomny}</span> : '')}</td>
                  <td style={{ ...bunka, fontSize: TYPO.sizes.xs }}>{row.ep_dorucene_sposob || ''}{row.ep_vyh_otvorene_at ? <div style={{ color: COLORS.muted }}>otvoril odkaz {casSk(row.ep_vyh_otvorene_at)}</div> : null}</td>
                  <td style={{ ...bunka, whiteSpace: 'nowrap' }}>
                    {pdfUrl[row.id]
                      ? <a href={pdfUrl[row.id]} target="_blank" rel="noreferrer" style={{ color: COLORS.nav, fontSize: TYPO.sizes.sm, marginRight: 8 }}>Otvoriť PDF</a>
                      : <BtnGhost size="sm" onClick={() => pripravPdf(row.id)}>PDF</BtnGhost>}
                    <BtnGhost size="sm" onClick={() => over(row.id)}>Overiť odtlačok</BtnGhost>
                    {data.vedenie && row.status === 'na_podpis' && !row.replaced_by && <BtnGhost size="sm" onClick={() => zrus(row)}>Zrušiť výzvu</BtnGhost>}
                    {ov && <div style={{ fontSize: TYPO.sizes.xs, color: ov.ok ? '#15803d' : COLORS.warn, marginTop: 4, whiteSpace: 'normal' }}>{ov.sprava}</div>}
                  </td>
                </tr>
              )
            })}</tbody>
          </table>
        )}

      <Modal open={nove} onClose={() => setNove(false)} isDirty={dirty} onDirtyClose={async () => {
          if (await confirm({ title: 'Zahodiť rozpísaný dokument?', message: 'Položky sa neuložia.', confirmLabel: 'Zahodiť' })) setNove(false)
        }}
        title="Prevzatie OOPP a náradia" subtitle={employeeName} width="min(900px, 95vw)"
        footer={<div style={{ display: 'flex', gap: SPACE.sm, justifyContent: 'flex-end' }}>
          <BtnGhost onClick={() => setNove(false)}>Zrušiť</BtnGhost>
          <Btn variant="add" loading={busy} onClick={vytvor}>Vytvoriť na podpis</Btn>
        </div>}>
        <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted, marginBottom: SPACE.md, lineHeight: 1.5 }}>
          <b>Povinné:</b> aspoň jedna položka s názvom. Matrix z toho vyrobí dokument na hlavičkovom papieri a zamestnanec ho podpíše
          osobne na podpisovej stránke. Kým ho nepodpíše, dá sa výzva zrušiť; podpísaný dokument sa už nemení ani nemaže.
          Dokument je potvrdenie o prevzatí a poučení — <b>nie je</b> dohodou o hmotnej zodpovednosti.
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: SPACE.md }}>
          <thead><tr>{['Položka *', 'Druh', 'Počet', 'Veľkosť / ev. č.', 'Poznámka', ''].map(h =>
            <th key={h} style={{ textAlign: 'left', fontSize: TYPO.sizes.xs, color: COLORS.muted, padding: '4px 6px' }}>{h}</th>)}</tr></thead>
          <tbody>{polozky.map((p, i) => (
            <tr key={i}>
              <td style={{ padding: 4 }}><input value={p.nazov} onChange={e => zmen(i, 'nazov', e.target.value)} placeholder="napr. Prilba ochranná" style={pole(240)} /></td>
              <td style={{ padding: 4 }}><select value={p.druh} onChange={e => zmen(i, 'druh', e.target.value)} style={pole(110)}><option value="oopp">OOPP</option><option value="naradie">náradie</option></select></td>
              <td style={{ padding: 4 }}><input type="number" min="1" max="999" value={p.pocet} onChange={e => zmen(i, 'pocet', e.target.value)} style={pole(64)} /></td>
              <td style={{ padding: 4 }}><input value={p.velkost} onChange={e => zmen(i, 'velkost', e.target.value)} style={pole(130)} /></td>
              <td style={{ padding: 4 }}><input value={p.pozn} onChange={e => zmen(i, 'pozn', e.target.value)} style={pole(180)} /></td>
              <td style={{ padding: 4 }}>{polozky.length > 1 && <BtnGhost size="sm" onClick={() => setPolozky(x => x.filter((_, j) => j !== i))}>Odobrať</BtnGhost>}</td>
            </tr>
          ))}</tbody>
        </table>
        <BtnGhost onClick={() => setPolozky(x => [...x, prazdny()])}>+ Ďalšia položka</BtnGhost>
        <div style={{ display: 'flex', gap: SPACE.lg, flexWrap: 'wrap', marginTop: SPACE.lg }}>
          <label style={{ fontSize: TYPO.sizes.sm }}>Dátum odovzdania<br /><input type="date" value={datum} onChange={e => setDatum(e.target.value)} style={pole(160)} /></label>
          <label style={{ fontSize: TYPO.sizes.sm }}>Pri podpise bude za KAMIR (meno, funkcia)<br />
            <input value={pritomny} onChange={e => setPritomny(e.target.value)} placeholder="prázdne = ty" style={pole(300)} /></label>
        </div>
      </Modal>
    </div>
  )
}

const pole = (w) => ({ width: w, maxWidth: '100%', padding: '6px 8px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontSize: 16, fontFamily: 'inherit' })
